import React from 'react';
import PropTypes from 'prop-types';
import Button from '../../../../../ui-kitchen/src/components/Button';
import Carousel from '../../../../../ui-kitchen/src/components/Carousel';
import ClampLines from '../../../../../ui-kitchen/src/components/ClampLines/ClampLines';
import Icon from '../../../../../ui-kitchen/src/components/Icon';
import ReservationButtonList from '../../../../../ui-kitchen/src/components/ReservationButtonList';
import TockBadge from '../../../../../ui-kitchen/src/components/TockBadge';
import { getTranslation } from '../../../../../ui-kitchen/src/providers/TranslationProvider';
import transformSlots from '../../../../../ui-kitchen/src/transformers/transformSlots';
import Close from '../../../../../ui-kitchen/src/svg/icons/ic_close_20x20.svg';
import Arrow from '../../../../../ui-kitchen/src/svg/icons/arrow.svg';
import PeopleIcon from '../../../../../ui-kitchen/src/svg/icons/ic_guests_20x20.svg';
import toLocaleLongDate from '../../utils/tockExperienceUtils';
import './TockExperienceOffering.scss';

const TockExperienceOffering = ({
  experience,
  isMobile,
  onRequestClose,
  venueName,
  selectedSeats,
  selectedDate,
  venueId,
  onReservationSelect,
  handleGoBackToOptions,
  hasBackButton,
}) => {
  const formattedDate = toLocaleLongDate(selectedDate);
  const formattedTime = experience?.event_time_display || '';
  const experienceSlots = experience?.slots || [];
  const transformedExperienceSlots = transformSlots(experienceSlots);

  const enrichedExperienceSlots = transformedExperienceSlots.map(
    (slot, index) => ({
      ...slot,
      token: experience?.id,
      tockBookingUrl: experienceSlots[index]?.tock_booking_url,
    })
  );

  const slideContent = (experience?.images || []).map((imageUrl, index) => ({
    id: `${experience?.id || 'experience'}-${index}`,
    card: (
      <img
        className="TockExperienceOffering__image"
        src={imageUrl}
        alt={experience?.name || getTranslation({ translationKey: 'offering_modal_default_experience_name', fallback: 'Experience' })}
      />
    ),
  }));

  return (
    <div className="TockExperienceOffering TockExperienceOfferingModal">
      <div className="TockExperienceOffering__title-bar">
        <div className="TockExperienceOffering__title-bar-inner">
          {hasBackButton && (
            <Button
              variant="no-style"
              className="TockExperienceOffering__back"
              data-test-id="offering-modal-button-back"
              onClick={handleGoBackToOptions}
              autoFocus
              aria-label={getTranslation({
                translationKey: 'offering_modal_back_label',
                fallback: 'Back to options',
              })}
            >
              <span aria-hidden="true">
                <Arrow aria-hidden="true" focusable="false" />
              </span>
            </Button>
          )}
          <Button
            variant="no-style"
            className="TockExperienceOffering__close"
            data-test-id="offering-modal-button-close"
            onClick={onRequestClose}
            autoFocus={!hasBackButton}
            aria-label={getTranslation({
              translationKey: 'offering_modal_close_label',
              fallback: 'Close Offering Modal',
            })}
          >
            <span aria-hidden="true">
              <Close aria-hidden="true" focusable="false" />
            </span>
          </Button>
          <h2 className="TockExperienceOffering__header">
            {venueName || getTranslation({ translationKey: 'offering_modal_default_venue_name', fallback: 'Venue' })}
          </h2>
          <div className="TockExperienceOffering__divider" />
        </div>
      </div>
      <div className="TockExperienceOffering__content">
        {slideContent.length > 0 && (
          <div className="TockExperienceOffering__carousel">
            <Carousel
              buttonPosition="bottom-right-connected"
              carouselId="offering-images"
              hasButtons
              hasDotGroup
              infinite={false}
              isMobile={isMobile}
              slideContent={slideContent}
              step={1}
              visibleSlides={1}
            />
          </div>
        )}
        {experience?.experience_type_display && (
          <div className="TockExperienceOffering__event">
            {experience.experience_type_display}
          </div>
        )}
        <div className="TockExperienceOffering__tock-badge">
          <TockBadge large />
        </div>
        <h3 className="TockExperienceOffering__name">
          {experience?.name || getTranslation({ translationKey: 'offering_modal_default_experience_name', fallback: 'Experience' })}
        </h3>
        {experience?.price_type_display && (
          <div className="TockExperienceOffering__pricing-label">
            {experience.price_type_display}
          </div>
        )}
        {experience?.event_time_display && (
          <div className="TockExperienceOffering__event-time">
            {experience.event_time_display}
          </div>
        )}
        {experience?.description && (
          <div className="TockExperienceOffering__description">
            <ClampLines
              text={experience.description}
              id={`offering-description-${experience?.id || 'experience'}`}
              lines={4}
              buttons
              moreText={getTranslation({ translationKey: 'venue_description_read_more', fallback: 'Read more' })}
              lessText={getTranslation({ translationKey: 'venue_description_read_less', fallback: 'Read less' })}
              ellipsis="..."
            />
          </div>
        )}
        {(selectedSeats || formattedDate) && (
          <div className="TockExperienceOffering__reservation-meta">
            <span className="sr-only">
              {getTranslation({
                translationKey: 'tock_experience_options_party_label',
                fallback: `Party of ${selectedSeats}, ${formattedDate}, ${formattedTime}`,
                translationValues: {
                  seats: String(selectedSeats),
                  date: formattedDate,
                  time: formattedTime,
                },
              })}
            </span>
            <span aria-hidden="true" className="TockExperienceOffering__reservation-meta-visual">
              {selectedSeats && (
                <span className="TockExperienceOffering__reservation-guests">
                  <Icon component={PeopleIcon} name="people" ariaHidden="true" />
                  <span>{selectedSeats}</span>
                </span>
              )}
              {selectedSeats && formattedDate && <span>&middot;</span>}
              {formattedDate}
            </span>
          </div>
        )}
        {enrichedExperienceSlots.length > 0 && (
          <ReservationButtonList
            className="TockExperienceOffering__booking-bricks"
            reservations={enrichedExperienceSlots}
            venueId={venueId}
            onReservationSelect={onReservationSelect}
            isTockInventory
            tockNotifyUrl={experience?.tock_notify_url}
          />
        )}
      </div>
    </div>
  );
};

TockExperienceOffering.propTypes = {
  experience: PropTypes.shape({
    description: PropTypes.string,
    event_time_display: PropTypes.string,
    experience_type_display: PropTypes.string,
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    images: PropTypes.arrayOf(
      PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.shape({
          id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
          image: PropTypes.string,
          src: PropTypes.string,
          url: PropTypes.string,
        }),
      ])
    ),
    name: PropTypes.string,
    price_type_display: PropTypes.string,
    slots: PropTypes.arrayOf(PropTypes.shape({})),
    tock_notify_url: PropTypes.string,
  }),
  isMobile: PropTypes.bool,
  onRequestClose: PropTypes.func,
  venueName: PropTypes.string,
  selectedSeats: PropTypes.number,
  selectedDate: PropTypes.string,
  venueId: PropTypes.number,
  onReservationSelect: PropTypes.func,
  handleGoBackToOptions: PropTypes.func,
  hasBackButton: PropTypes.bool,
};

TockExperienceOffering.defaultProps = {
  experience: null,
  isMobile: false,
  onRequestClose: () => { },
  venueName: null,
  selectedSeats: null,
  selectedDate: null,
  venueId: null,
  onReservationSelect: () => { },
  handleGoBackToOptions: () => { },
  hasBackButton: false,
};

TockExperienceOffering.displayName = 'TockExperienceOffering';

export default TockExperienceOffering;
